import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Alert, Button, Container, Spinner } from "reactstrap";

import AdatisLogo from "../../../Components/Common/AdatisLogo";
import {
  confirmPublicCsNurtureUnsubscribe,
  getPublicCsNurtureUnsubscribe,
  PublicCsUnsubscribeData,
} from "../../../services/publicCsNurtureUnsubscribeService";

const PublicCsNurtureUnsubscribePage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") ?? "";

  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PublicCsUnsubscribeData | null>(null);
  const [done, setDone] = useState(false);

  document.title = t("public_cs_unsubscribe.pageTitle");

  const load = useCallback(async (): Promise<void> => {
    if (!token) {
      setInvalid(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setInvalid(false);

    try {
      const response = await getPublicCsNurtureUnsubscribe(token);
      setData(response.data ?? null);
      if (response.data?.already_opted_out) {
        setDone(true);
      }
    } catch (err) {
      const apiError = err as { code?: string };
      if (apiError.code === "CS_NURTURE_UNSUBSCRIBE_TOKEN_INVALID") {
        setInvalid(true);
        return;
      }
      setError(t("public_cs_unsubscribe.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [t, token]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleConfirm = async (): Promise<void> => {
    setActing(true);
    setError(null);

    try {
      const response = await confirmPublicCsNurtureUnsubscribe(token);
      setData(response.data ?? null);
      setDone(true);
    } catch (err) {
      const apiError = err as { code?: string };
      if (apiError.code === "CS_NURTURE_UNSUBSCRIBE_TOKEN_INVALID") {
        setInvalid(true);
        return;
      }
      setError(t("public_cs_unsubscribe.errors.confirm"));
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="auth-page-wrapper py-5 d-flex justify-content-center align-items-center min-vh-100">
      <Container style={{ maxWidth: 480 }}>
        <div className="text-center mb-4">
          <AdatisLogo variant="light" />
        </div>

        <div className="bg-white rounded-3 shadow-sm p-4 p-md-5">
          <h1 className="h4 mb-3">{t("public_cs_unsubscribe.heading")}</h1>

          {loading && (
            <div className="text-center py-4">
              <Spinner />
            </div>
          )}

          {!loading && invalid && (
            <Alert color="warning" className="mb-0">
              {t("public_cs_unsubscribe.errors.invalid")}
            </Alert>
          )}

          {!loading && error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {!loading && !invalid && data && (
            <>
              <p className="text-muted mb-3">
                {data.tenant_name
                  ? t("public_cs_unsubscribe.forTenant", { tenant: data.tenant_name })
                  : null}
              </p>
              <p className="mb-4">{data.message}</p>
              {!done && (
                <Button color="dark" disabled={acting} onClick={() => void handleConfirm()}>
                  {acting ? <Spinner size="sm" /> : t("public_cs_unsubscribe.confirm")}
                </Button>
              )}
              {done && (
                <Alert color="success" className="mb-0">
                  {t("public_cs_unsubscribe.done")}
                </Alert>
              )}
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default PublicCsNurtureUnsubscribePage;
